// The one place an @-mention turns into a notification.
//
// Every screen that lets someone type "@Kasandra Reyes" routes through here:
// the Tuesday call sheet, deal notes, feed posts, feed comments and deal chat.
// Keeping it in one function is what makes the promise the same everywhere —
// tag a teammate and they get the bell, the phone push and an email.
//
// Two rules earn their keep:
//
//   * previousText. Deal notes autosave every few seconds, and a call-sheet
//     note can be edited all week. Diffing against what the text said last
//     time means a name already sitting in the note is old news, so editing a
//     note around an existing tag never pesters that person again.
//   * Delivery never blocks the save. A missing Gmail token or a dropped
//     network call is logged and swallowed; the note itself is already
//     written by the time we get here, and losing a notification must never
//     look to the writer like losing their work.
import { extractMentionedIds, getFullName } from "./mentions";
import { newmessagesuccess } from "./helper";
import { sendMentionEmail } from "./emailSender";

// Opt-out, not opt-in: a teammate who has never touched the setting still
// hears about being tagged. Only an explicit false turns mention email off.
export function wantsMentionEmail(u) {
  return u?.emailOnMention !== false && !!u?.email;
}

export async function notifyMentions({
  text,
  previousText = "",
  users,
  sender,
  where,
  dealId,
}) {
  const already = extractMentionedIds(previousText, users);
  const fresh = [...extractMentionedIds(text, users)].filter(
    (id) => !already.has(id) && id !== sender?.id,
  );
  if (fresh.length === 0) return [];

  const senderName = getFullName(sender) || "Someone";
  const title = "You were mentioned";
  const line = `${senderName} mentioned you in ${where}`;
  const ref = String(dealId || "");

  await Promise.all(
    fresh.map(async (id) => {
      const target = (users || []).find((u) => u.id === id);

      // Bell + phone push. Its own catch so one bad token can't stop the
      // rest of the list, or the email below, from going out.
      await newmessagesuccess(title, id, line, ref, "mention", ref).catch(
        (err) => console.error("Mention push failed:", err),
      );

      if (!wantsMentionEmail(target)) return;
      try {
        const res = await sendMentionEmail({
          userId: sender?.id,
          to: target.email,
          toName: getFullName(target),
          senderName,
          where,
          text,
          relatedDealId: ref,
        });
        if (!res?.ok) console.error("Mention email failed:", res?.error);
      } catch (err) {
        console.error("Mention email failed:", err);
      }
    }),
  );

  return fresh;
}
